/**
 * Datos inventados (convención #21) para la preview del cierre semestral.
 * Las casas 9001-9004 no existen en el barrio.
 */
import type {
  CierreSemestral,
  CierreSemestralDetalle,
} from '../../lib/api/cierres-semestrales';

const INGRESOS_S1 = [
  { tipo: 'Cuotas', monto: 1_650_000, color: '#5fd9b1' },
  { tipo: 'Controles remotos', monto: 120_000, color: '#6fd3e0' },
  { tipo: 'Multas', monto: 30_000, color: '#ffb775' },
];
const GASTOS_S1 = [
  { tipo: 'Seguridad', monto: 960_000, color: '#ff7a7a' },
  { tipo: 'Mantención', monto: 310_000, color: '#b89bff' },
  { tipo: 'Administración', monto: 85_000, color: '#8a8479' },
];
const INGRESOS_S2 = [
  { tipo: 'Cuotas', monto: 1_890_000, color: '#5fd9b1' },
  { tipo: 'Controles remotos', monto: 60_000, color: '#6fd3e0' },
];
const GASTOS_S2 = [
  { tipo: 'Seguridad', monto: 990_000, color: '#ff7a7a' },
  { tipo: 'Mantención', monto: 540_000, color: '#b89bff' },
  { tipo: 'Administración', monto: 90_000, color: '#8a8479' },
];

const casa = (id: string, cuotas: [string, string, number][]) => ({
  lote_id: `lote-${id}`,
  lote_identificador: id,
  cuotas_afectadas: cuotas.map(([periodo, etiqueta, saldo], i) => ({
    cuota_id: `cuota-${id}-${i}`,
    periodo,
    etiqueta,
    monto_total: 30_000,
    monto_aplicado: 30_000 - saldo,
    monto_moroso: saldo,
  })),
  monto_moroso_total: cuotas.reduce((s, [, , saldo]) => s + saldo, 0),
});

export const CIERRE_S1_MOCK: CierreSemestralDetalle = {
  id: 'cierre-2026-s1',
  periodo: '2026-S1',
  etiqueta: '1° semestre 2026',
  fecha_corte: '2026-06-30',
  cerrado_at: '2026-07-03T14:20:00Z',
  cerrado_por_usuario_id: 'usuario-ejemplo',
  ingresos: 1_800_000,
  gastos: 1_355_000,
  resultado: 445_000,
  saldo_inicial: 2_100_000,
  saldo_inicial_fecha: '2025-12-31',
  saldo_final: 2_480_000,
  saldo_final_fecha: '2026-06-30',
  saldo_final_banco: 2_469_500,
  saldo_final_banco_fecha: '2026-06-27',
  total_lotes_morosos: 3,
  total_monto_moroso: 70_000,
  acta_url: null,
  ingresos_por_tipo: INGRESOS_S1,
  gastos_por_categoria: GASTOS_S1,
  ajustes: [],
  resumen_anual: null,
  casas: [
    casa('9003', [
      ['2025-PRE', 'Deuda 2025', 10_000],
      ['2026-S1', '1° semestre 2026', 30_000],
    ]),
    casa('9001', [['2026-S1', '1° semestre 2026', 20_000]]),
    casa('9004', [['2026-S1', '1° semestre 2026', 10_000]]),
  ],
};

export const CIERRE_S2_MOCK: CierreSemestralDetalle = {
  id: 'cierre-2026-s2',
  periodo: '2026-S2',
  etiqueta: '2° semestre 2026',
  fecha_corte: '2026-12-31',
  cerrado_at: '2027-01-04T12:05:00Z',
  cerrado_por_usuario_id: 'usuario-ejemplo',
  ingresos: 1_950_000,
  gastos: 1_620_000,
  resultado: 330_000,
  saldo_inicial: 2_480_000,
  saldo_inicial_fecha: '2026-06-30',
  saldo_final: 2_790_000,
  saldo_final_fecha: '2026-12-31',
  saldo_final_banco: 2_790_000,
  saldo_final_banco_fecha: '2026-12-28',
  total_lotes_morosos: 2,
  total_monto_moroso: 40_000,
  acta_url: null,
  ingresos_por_tipo: INGRESOS_S2,
  gastos_por_categoria: GASTOS_S2,
  ajustes: [
    { periodo: '2026-S1', etiqueta: '1° semestre 2026', ingresos: 20_000, gastos: 0 },
  ],
  resumen_anual: {
    anio: 2026,
    ingresos: 3_770_000,
    gastos: 2_975_000,
    ingresos_por_tipo: [
      { tipo: 'Cuotas', monto: 3_560_000, color: '#5fd9b1' },
      { tipo: 'Controles remotos', monto: 180_000, color: '#6fd3e0' },
      { tipo: 'Multas', monto: 30_000, color: '#ffb775' },
    ],
    gastos_por_categoria: [
      { tipo: 'Seguridad', monto: 1_950_000, color: '#ff7a7a' },
      { tipo: 'Mantención', monto: 850_000, color: '#b89bff' },
      { tipo: 'Administración', monto: 175_000, color: '#8a8479' },
    ],
    saldo_inicial: 2_100_000,
    saldo_inicial_fecha: '2025-12-31',
    saldo_final: 2_790_000,
    saldo_final_fecha: '2026-12-31',
    saldo_final_banco: 2_790_000,
    saldo_final_banco_fecha: '2026-12-28',
  },
  casas: [
    casa('9003', [
      ['2025-PRE', 'Deuda 2025', 10_000],
      ['2026-S2', '2° semestre 2026', 20_000],
    ]),
    casa('9002', [['2026-S2', '2° semestre 2026', 10_000]]),
  ],
};

const resumen = (d: CierreSemestralDetalle): CierreSemestral => {
  const {
    ingresos_por_tipo: _i,
    gastos_por_categoria: _g,
    ajustes: _a,
    resumen_anual: _r,
    casas: _c,
    ...base
  } = d;
  return base;
};

export const CIERRES_MOCK: CierreSemestral[] = [
  resumen(CIERRE_S2_MOCK),
  resumen(CIERRE_S1_MOCK),
];

export const DETALLES_MOCK: Record<string, CierreSemestralDetalle> = {
  [CIERRE_S1_MOCK.id]: CIERRE_S1_MOCK,
  [CIERRE_S2_MOCK.id]: CIERRE_S2_MOCK,
};
